import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabase";
import { withRetry } from "../utils/supabaseErrorHandler";
import "./TripProfitability.css";

const parseNumber = (value) => {
  const num = Number(String(value ?? "").replace(",", "."));
  return Number.isNaN(num) ? 0 : num;
};

const formatMoney = (value) =>
  parseNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

const formatDateFr = (isoDate) => {
  if (!isoDate) return "—";
  const d = new Date(isoDate);
  return d.toLocaleDateString("fr-FR");
};

const monthStartIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
};

const todayIso = () => new Date().toISOString().split("T")[0];

const computeTripTotals = (productPrices) => {
  const lines = Array.isArray(productPrices) ? productPrices : [];
  return lines.reduce(
    (acc, item) => {
      const qty = parseNumber(item.quantity);
      const sell = parseNumber(item.price);
      const buy = parseNumber(item.prix_usine);
      const revenue = qty * sell;
      const cost = qty * buy;
      return {
        revenue: acc.revenue + revenue,
        cost: acc.cost + cost,
        margin: acc.margin + (revenue - cost),
      };
    },
    { revenue: 0, cost: 0, margin: 0 }
  );
};

const TripProfitability = () => {
  const [trucks, setTrucks] = useState([]);
  const [trips, setTrips] = useState([]);
  const [fuelRows, setFuelRows] = useState([]);
  const [truckId, setTruckId] = useState("");
  const [dateFrom, setDateFrom] = useState(monthStartIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTruckId, setExpandedTruckId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [trucksRes, tripsRes, fuelRes] = await Promise.all([
        withRetry(() =>
          supabase.from("trucks").select("id, immatriculation").order("immatriculation", { ascending: true })
        ),
        withRetry(() =>
          supabase
            .from("trips")
            .select("id, truck_id, destination, date, cargo, product_prices, status")
            .eq("status", "completed")
            .gte("date", dateFrom)
            .lte("date", dateTo)
            .order("date", { ascending: false })
        ),
        withRetry(() =>
          supabase
            .from("fuel_history")
            .select("id, truck_id, raw_date, liters, cost")
            .gte("raw_date", dateFrom)
            .lte("raw_date", dateTo)
        ),
      ]);

      if (trucksRes.error) throw trucksRes.error;
      if (tripsRes.error) throw tripsRes.error;
      if (fuelRes.error) throw fuelRes.error;

      setTrucks(trucksRes.data || []);
      setTrips(tripsRes.data || []);
      setFuelRows(fuelRes.data || []);
    } catch (err) {
      console.error("Error loading profitability data:", err);
      setError("Échec du chargement des données de rentabilité.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const truckMap = useMemo(() => {
    const map = {};
    trucks.forEach((t) => {
      map[t.id] = t.immatriculation || t.id;
    });
    return map;
  }, [trucks]);

  const reports = useMemo(() => {
    const byTruck = {};

    const ensure = (id) => {
      const key = id || "__unassigned__";
      if (!byTruck[key]) {
        byTruck[key] = {
          truckId: id || null,
          label: id ? truckMap[id] || "Camion inconnu" : "Non assigné",
          tripCount: 0,
          revenue: 0,
          productCost: 0,
          productMargin: 0,
          fuelLiters: 0,
          fuelCost: 0,
          trips: [],
        };
      }
      return byTruck[key];
    };

    trips.forEach((trip) => {
      if (truckId && trip.truck_id !== truckId) return;
      const row = ensure(trip.truck_id);
      const totals = computeTripTotals(trip.product_prices);
      row.tripCount += 1;
      row.revenue += totals.revenue;
      row.productCost += totals.cost;
      row.productMargin += totals.margin;
      row.trips.push({
        ...trip,
        ...totals,
      });
    });

    fuelRows.forEach((fuel) => {
      if (truckId && fuel.truck_id !== truckId) return;
      if (!fuel.truck_id) return;
      const row = ensure(fuel.truck_id);
      row.fuelLiters += parseNumber(fuel.liters);
      row.fuelCost += parseNumber(fuel.cost);
    });

    return Object.values(byTruck)
      .map((row) => ({
        ...row,
        profit: row.productMargin - row.fuelCost,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [trips, fuelRows, truckId, truckMap]);

  const grandTotal = useMemo(
    () =>
      reports.reduce(
        (acc, row) => ({
          tripCount: acc.tripCount + row.tripCount,
          revenue: acc.revenue + row.revenue,
          productCost: acc.productCost + row.productCost,
          productMargin: acc.productMargin + row.productMargin,
          fuelCost: acc.fuelCost + row.fuelCost,
          fuelLiters: acc.fuelLiters + row.fuelLiters,
          profit: acc.profit + row.profit,
        }),
        {
          tripCount: 0,
          revenue: 0,
          productCost: 0,
          productMargin: 0,
          fuelCost: 0,
          fuelLiters: 0,
          profit: 0,
        }
      ),
    [reports]
  );

  return (
    <div className="trip-profit-container">
      <aside className="sidebar">
        <h2 className="trip-profit-fleet-title">Système de Gestion & Contrôle</h2>
        <nav>
          <ul>
            <li><Link to="/fleet/dashboard">📊 Gestion de Flotte</Link></li>
            <li><Link to="/cash-tracking">💵 Gestion de Caisse</Link></li>
            <li><Link to="/parc">🔧 Gestion des Pièces</Link></li>
            <li><Link to="/fleet/stock-carburant">⛽ Stock Carburant</Link></li>
            <li><Link to="/stock">📦 Gestion de Stock</Link></li>
            <li><Link to="/schedule">🗓️ Gestion des Programmes</Link></li>
            <li><Link to="/schedule/history">📜 Historique voyages</Link></li>
            <li className="active"><Link to="/schedule/profitability">📈 Rentabilité voyages</Link></li>
            <li><Link to="/maintenance">🛠️ Maintenance</Link></li>
            <li><Link to="/trailers">🚛 Gestion des Remorques</Link></li>
            <li><Link to="/incidents">🚨 Gestion des Incidents</Link></li>
            <li><Link to="/driver-payments">💰 Gestion des salaires </Link></li>
            <li><Link to="/chatbot">🤖 Système de Reporting</Link></li>
          </ul>
        </nav>
        <div className="trip-profit-sidebar-footer">
          <p>Version 1.2.0</p>
          <p>© 2025</p>
        </div>
      </aside>

      <main className="trip-profit-content">
        <header className="trip-profit-header">
          <div>
            <h1>📈 Rentabilité des voyages</h1>
            <p className="trip-profit-subtitle">
              Voyages terminés (marge produits) − carburant sur la période.
              Le carburant est par camion / période, pas par voyage.
            </p>
          </div>
          <div className="trip-history-header-actions">
            <Link to="/schedule/history" className="trip-profit-back-btn">
              📜 Historique
            </Link>
            <Link to="/schedule" className="trip-profit-back-btn">
              ← Programmes
            </Link>
          </div>
        </header>

        <section className="trip-profit-filters">
          <div className="trip-profit-filter-group">
            <label htmlFor="profitTruck">Camion</label>
            <select
              id="profitTruck"
              value={truckId}
              onChange={(e) => setTruckId(e.target.value)}
            >
              <option value="">Tous les camions</option>
              {trucks.map((truck) => (
                <option key={truck.id} value={truck.id}>
                  {truck.immatriculation}
                </option>
              ))}
            </select>
          </div>
          <div className="trip-profit-filter-group">
            <label htmlFor="profitFrom">Du</label>
            <input
              id="profitFrom"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="trip-profit-filter-group">
            <label htmlFor="profitTo">Au</label>
            <input
              id="profitTo"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <button type="button" className="trip-profit-refresh-btn" onClick={fetchData}>
            Actualiser
          </button>
        </section>

        {error && <div className="trip-profit-error">{error}</div>}
        {loading && <p>Chargement...</p>}

        {!loading && (
          <>
            <section className="trip-profit-summary">
              <div className="trip-profit-stat">
                <span>Voyages terminés</span>
                <strong>{grandTotal.tripCount}</strong>
              </div>
              <div className="trip-profit-stat">
                <span>Ventes</span>
                <strong>{formatMoney(grandTotal.revenue)}</strong>
              </div>
              <div className="trip-profit-stat">
                <span>Coût usine</span>
                <strong>{formatMoney(grandTotal.productCost)}</strong>
              </div>
              <div className="trip-profit-stat">
                <span>Marge produits</span>
                <strong>{formatMoney(grandTotal.productMargin)}</strong>
              </div>
              <div className="trip-profit-stat">
                <span>Carburant</span>
                <strong>{formatMoney(grandTotal.fuelCost)}</strong>
              </div>
              <div className={`trip-profit-stat ${grandTotal.profit >= 0 ? "positive" : "negative"}`}>
                <span>Résultat</span>
                <strong>{formatMoney(grandTotal.profit)}</strong>
              </div>
            </section>

            {reports.length === 0 ? (
              <p className="trip-profit-empty">
                Aucun voyage terminé ni carburant sur cette période.
                Utilisez « Terminer » sur un voyage pour l&apos;archiver ici.
              </p>
            ) : (
              <section className="trip-profit-table-wrap">
                <table className="trip-profit-table">
                  <thead>
                    <tr>
                      <th>Camion</th>
                      <th>Voyages</th>
                      <th>Ventes</th>
                      <th>Coût usine</th>
                      <th>Marge</th>
                      <th>Carburant (L)</th>
                      <th>Carburant (TND)</th>
                      <th>Résultat</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((row) => {
                      const key = row.truckId || "__unassigned__";
                      const open = expandedTruckId === key;
                      return (
                        <React.Fragment key={key}>
                          <tr>
                            <td>{row.label}</td>
                            <td>{row.tripCount}</td>
                            <td>{formatMoney(row.revenue)}</td>
                            <td>{formatMoney(row.productCost)}</td>
                            <td>{formatMoney(row.productMargin)}</td>
                            <td>{formatMoney(row.fuelLiters)}</td>
                            <td>{formatMoney(row.fuelCost)}</td>
                            <td className={row.profit >= 0 ? "positive" : "negative"}>
                              {formatMoney(row.profit)}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="trip-profit-expand-btn"
                                onClick={() =>
                                  setExpandedTruckId(open ? null : key)
                                }
                                disabled={row.trips.length === 0}
                              >
                                {open ? "Masquer" : "Détails"}
                              </button>
                            </td>
                          </tr>
                          {open && (
                            <tr className="trip-profit-detail-row">
                              <td colSpan={9}>
                                <table className="trip-profit-inner-table">
                                  <thead>
                                    <tr>
                                      <th>Date</th>
                                      <th>Destination</th>
                                      <th>Cargaison</th>
                                      <th>Produits</th>
                                      <th>Ventes</th>
                                      <th>Usine</th>
                                      <th>Marge</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {row.trips.map((trip) => (
                                      <tr key={trip.id}>
                                        <td>{formatDateFr(trip.date)}</td>
                                        <td>{trip.destination}</td>
                                        <td>{trip.cargo}</td>
                                        <td>
                                          {(trip.product_prices || []).length === 0
                                            ? "—"
                                            : (trip.product_prices || [])
                                                .map(
                                                  (p) =>
                                                    `${p.product_name}×${parseNumber(p.quantity)}`
                                                )
                                                .join(", ")}
                                        </td>
                                        <td>{formatMoney(trip.revenue)}</td>
                                        <td>{formatMoney(trip.cost)}</td>
                                        <td>{formatMoney(trip.margin)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default TripProfitability;
